import { Activity, HeartPulse, Repeat } from 'lucide-react'

import { IndicadorPendente } from './components/indicador-pendente'
import { TaxaCard } from './components/taxa-card'
import { farol } from './farol'
import { ROTULO_SITUACAO, type Cadencia, type Operacao, type SaudeBase } from './types'

import type { Rag } from '@/features/relatorio/types'
import type { LucideIcon } from 'lucide-react'
import type { ReactElement, ReactNode } from 'react'

import { Card } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import { Explicacao } from '@/features/relatorio/components/explicacao'
import { KpiCard } from '@/features/relatorio/components/kpi-card'
import { TituloComFonte } from '@/features/relatorio/components/titulo-com-fonte'
import { cn } from '@/lib/cn'
import { inteiro, percentual } from '@/lib/formato'

interface FaixaProps<T> {
  dados: T
  /** Amplia cards e reduz colunas — blocos com poucos indicadores no telão. */
  telao?: boolean
}

/** Cor da barra por banda do farol — mesma escala dos cards. */
const COR_DA_BARRA: Record<Rag, string> = {
  verde: 'bg-success',
  amarelo: 'bg-warning',
  vermelho: 'bg-destructive',
  'sem-meta': 'bg-primary',
}

/** Metas dos indicadores — base do farol de cada card. */
const META_RENOVACAO = 0.9
/** Retenção alvo de 95% — ou seja, churn de até 5% no período. */
const META_RETENCAO = 0.95
/** NPS alvo 9,0 na escala 0–10, normalizado para fração. */
const META_NPS = 0.9
const META_SLA = 0.9
/** Cadência é binária: ou o relatório saiu, ou não saiu. */
const META_RT = 1

/**
 * Cada bloco vira uma FAIXA, não uma tela. Com 1–4 indicadores por bloco, três
 * telas separadas ficariam vazias; empilhadas, o conjunto lê como um painel só e
 * ainda deixa claro onde cada bloco começa (título + pergunta-guia + regra).
 */
function Faixa({
  titulo,
  pergunta,
  icone: Icone,
  telao,
  children,
}: {
  titulo: string
  pergunta: string
  icone: LucideIcon
  telao?: boolean
  children: ReactNode
}): ReactElement {
  return (
    <section className="space-y-3">
      <div className="border-border flex items-baseline gap-2 border-b pb-2">
        <Icone className="text-muted-foreground h-4 w-4 shrink-0 self-center" aria-hidden />
        <h2 className={cn('font-semibold tracking-tight', telao ? 'text-2xl' : 'text-base')}>
          {titulo}
        </h2>
        <p className={cn('text-muted-foreground truncate italic', telao ? 'text-lg' : 'text-sm')}>
          {pergunta}
        </p>
      </div>
      {children}
    </section>
  )
}

export function FaixaSaudeBase({ dados, telao }: FaixaProps<SaudeBase>): ReactElement {
  const tamanho = telao ? 'telao' : 'normal'
  return (
    <Faixa titulo="Saúde da Base" pergunta="A base está saudável?" icone={HeartPulse} telao={telao}>
      <div className={cn('grid gap-3 sm:grid-cols-2', telao ? 'gap-5' : 'xl:grid-cols-4')}>
        <KpiCard
          rotulo="Base ativa"
          valor={inteiro(dados.baseAtiva)}
          cadencia="mensal"
          origem="clickup"
          detalhe={`${inteiro(dados.entradasNoPeriodo)} entrada(s) no período`}
          explicacao={{
            fonte: 'ClickUp',
            oQueE: 'Quantos membros estão ativos hoje nas listas de membros configuradas.',
            formula: 'contagem de membros com situação ativa ou em não-renovação',
            origem: 'listas de membros · status',
            regra:
              'Não-renovação ainda conta como base ativa: o contrato segue valendo até o fim. Situação desconhecida entra como "outros", nunca como ativo.',
          }}
          tamanho={tamanho}
        />
        <KpiCard
          rotulo="Taxa de renovação"
          valor={
            dados.renovacao.taxa === null ? '—' : percentual(dados.renovacao.taxa, 0)
          }
          cadencia="mensal"
          origem="clickup"
          rag={farol(dados.renovacao.taxa, META_RENOVACAO)}
          progresso={dados.renovacao.taxa ?? undefined}
          detalhe={
            dados.renovacao.previstas === 0
              ? 'Nenhuma renovação prevista no período'
              : `${dados.renovacao.renovadas} de ${dados.renovacao.previstas} renovaram`
          }
          explicacao={{
            fonte: 'ClickUp',
            oQueE: 'Dos contratos com renovação marcada para o período, quantos seguem ativos.',
            formula: 'membros ativos com renovação na janela ÷ membros com renovação na janela',
            origem: 'listas de membros · Data de Renovação · status',
            regra:
              'Mede só quem TINHA renovação marcada no período. Usar a base inteira como denominador incluiria contratos que nem venceram e inflaria a taxa.',
          }}
          tamanho={tamanho}
        />
        {dados.churnMensalDisponivel ? (
          <KpiCard
            rotulo="Churn mensal"
            valor={dados.churn.taxa === null ? '—' : percentual(dados.churn.taxa, 1)}
            cadencia="mensal"
            origem="clickup"
            // Farol pela RETENÇÃO (1 − churn): churn alto tem que pintar vermelho.
            rag={farol(dados.churn.taxa === null ? null : 1 - dados.churn.taxa, META_RETENCAO)}
            detalhe={`${inteiro(dados.churn.saidas)} saída(s) sobre ${inteiro(dados.churn.baseInicial)} no início`}
            explicacao={{
              fonte: 'ClickUp',
              oQueE: 'Quantos membros saíram no período, sobre a base ativa no início dele.',
              formula: 'membros com data de saída na janela ÷ base ativa no início da janela',
              origem: 'listas de membros · campo Data de Saída',
              regra:
                'A base inicial soma de volta quem saiu na janela — usar a base de hoje como denominador subestimaria o churn, porque quem saiu já não está nela.',
            }}
            tamanho={tamanho}
          />
        ) : (
          <IndicadorPendente
            rotulo="Churn mensal"
            cadencia="mensal"
            fonteEsperada={'ClickUp · campo "Data de Saída"'}
            detalhe={`${inteiro(dados.emChurnHoje)} em churn hoje (posição, não fluxo do mês)`}
            motivo={
              'Crie um campo de data chamado "Data de Saída" na lista de membros e preencha ao mover para churn. O painel passa a calcular o churn do período sozinho, sem alteração no sistema.'
            }
            tamanho={tamanho}
          />
        )}
        {dados.npsDisponivel ? (
          <KpiCard
            rotulo="NPS de membro"
            valor={dados.nps.media === null ? '—' : dados.nps.media.toFixed(1)}
            cadencia="mensal"
            origem="clickup"
            rag={farol(dados.nps.media === null ? null : dados.nps.media / 10, META_NPS)}
            detalhe={`${inteiro(dados.nps.respostas)} de ${inteiro(dados.nps.base)} responderam`}
            explicacao={{
              fonte: 'ClickUp',
              oQueE: 'Nota média de NPS informada pelos membros.',
              formula: 'média das notas preenchidas (escala 0–10)',
              origem: 'listas de membros · campo NPS',
              regra:
                'Só entra quem tem nota: contar quem não respondeu como zero afundaria a média e faria falta de coleta parecer insatisfação.',
            }}
            tamanho={tamanho}
          />
        ) : (
          <IndicadorPendente
            rotulo="NPS de membro"
            cadencia="mensal"
            fonteEsperada={'ClickUp · campo "NPS" na lista de membros'}
            motivo={
              'Crie um campo numérico chamado "NPS" na lista de membros e preencha a nota (0–10). O painel passa a calcular a média sozinho, sem alteração no sistema.'
            }
            tamanho={tamanho}
          />
        )}
      </div>

      <Card className="space-y-3 p-4 sm:p-5">
        <TituloComFonte titulo="Composição da base" origem="clickup" campo="status">
          <Explicacao
            rotulo="Composição da base"
            fonte="ClickUp"
            oQueE="Como a base se distribui entre as situações cadastradas."
            formula="contagem de membros por situação"
            origem="listas de membros · status"
            regra={`Posição de hoje, não fluxo do período. "${ROTULO_SITUACAO.churn}" mostra quantos estão marcados assim agora — não quantos saíram no mês.`}
          />
        </TituloComFonte>
        {dados.composicao.length === 0 ? (
          <EmptyState
            size="sm"
            title="Nenhum membro nas listas configuradas"
            description="Confira CLICKUP_LISTAS_MEMBROS no ambiente da API."
          />
        ) : (
          <ul className="flex flex-wrap gap-2">
            {dados.composicao.map((item) => (
              <li
                key={item.situacao}
                className="border-border flex items-baseline gap-2 rounded-md border px-3 py-1.5"
              >
                <span className="text-muted-foreground text-xs">
                  {ROTULO_SITUACAO[item.situacao]}
                </span>
                <span className="font-mono text-sm font-semibold tabular-nums">
                  {inteiro(item.total)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </Faixa>
  )
}

export function FaixaOperacao({ dados, telao }: FaixaProps<Operacao>): ReactElement {
  const tamanho = telao ? 'telao' : 'normal'
  return (
    <Faixa titulo="Operação" pergunta="A operação está entregando?" icone={Activity} telao={telao}>
      <div className={cn('grid gap-3 sm:grid-cols-2', telao ? 'gap-5' : 'xl:grid-cols-4')}>
        <TaxaCard
          rotulo="Entregas no prazo (SLA)"
          taxa={dados.sla}
          meta={META_SLA}
          cadencia="semanal"
          unidade="entregas com prazo"
          explicacao={{
            fonte: 'ClickUp',
            oQueE: 'Das tarefas com prazo na janela, quantas foram concluídas até o prazo.',
            formula: 'tarefas concluídas até o vencimento ÷ tarefas com prazo',
            origem: 'listas de cadência · due date · data de conclusão',
            regra:
              'Conclusão é reconhecida pelo TIPO do status (concluído), não pelo nome — os rótulos variam por lista (feito, concluido, complete). Tarefa sem prazo não entra no cálculo.',
          }}
          tamanho={tamanho}
        />
        <TaxaCard
          rotulo="Eficácia operacional"
          taxa={dados.eficacia}
          meta={META_SLA}
          cadencia="semanal"
          unidade="planejadas"
          explicacao={{
            fonte: 'ClickUp',
            oQueE: 'Das entregas planejadas para a semana, quantas saíram no prazo.',
            formula: 'planejadas concluídas no prazo ÷ planejadas com prazo',
            origem: 'listas de cadência · campo Tipo de tarefa',
            regra:
              'Critério objetivo, não nota subjetiva. Tarefa com o tipo em branco fica FORA do denominador: contá-la como planejada derrubaria a taxa sem que nada tivesse piorado.',
          }}
          tamanho={tamanho}
        />
        <IndicadorPendente
          rotulo="NPS do último evento"
          cadencia="por evento"
          fonteEsperada="Forms / MemberHub"
          motivo="O NPS por evento é coletado fora dos sistemas integrados. No ClickUp ele aparece só como tarefa de consolidação, sem o número — e não há definição de qual é o “último evento”."
          tamanho={tamanho}
        />
        <IndicadorPendente
          rotulo="Público vs. capacidade"
          cadencia="por evento"
          fonteEsperada="MemberHub"
          motivo="Presença e capacidade por evento não existem em sistema integrado. O MemberHub ainda é um projeto em desenvolvimento, sem API disponível."
          tamanho={tamanho}
        />
      </div>

      <Card className="space-y-3 p-4 sm:p-5">
        <TituloComFonte titulo="SLA por área" origem="clickup" campo="Setor">
          <Explicacao
            rotulo="SLA por área"
            fonte="ClickUp"
            oQueE="Como cada área está entregando dentro do prazo."
            formula="por área: concluídas no prazo ÷ tarefas com prazo"
            origem="listas de cadência · campo Setor"
            regra="Ordenado do pior para o melhor — quem precisa de atenção aparece primeiro. Tarefa sem área definida não entra."
          />
        </TituloComFonte>
        {dados.slaPorSetor.length === 0 ? (
          <EmptyState
            size="sm"
            title="Nenhuma tarefa com área e prazo na janela"
            description="Sem Setor preenchido não há como abrir o SLA por área."
          />
        ) : (
          <ul className="space-y-2">
            {dados.slaPorSetor.map((item) => (
              <li key={item.setor}>
                <div className="flex items-baseline justify-between gap-2 text-sm">
                  <span className="min-w-0 truncate font-medium">{item.setor}</span>
                  <span className="text-muted-foreground shrink-0 tabular-nums">
                    {percentual(item.taxa, 0)} · {item.noPrazo}/{item.total}
                  </span>
                </div>
                <div className="bg-muted mt-1 h-2 overflow-hidden rounded-full">
                  <div
                    className={cn('h-full rounded-full', COR_DA_BARRA[farol(item.taxa, META_SLA)])}
                    style={{ width: `${Math.max(2, item.taxa * 100)}%` }}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}

        {/* Cobertura baixa significa taxa apurada sobre uma fatia — o painel diz. */}
        {dados.coberturaTipo.taxa !== null && dados.coberturaTipo.taxa < 1 ? (
          <p className="text-muted-foreground border-border border-t pt-3 text-xs">
            {percentual(dados.coberturaTipo.taxa, 0)} das {inteiro(dados.tarefas)} tarefas têm o
            campo “Tipo de tarefa” preenchido — a eficácia enxerga só essa fatia.
          </p>
        ) : null}
      </Card>
    </Faixa>
  )
}

export function FaixaCadencia({ dados, telao }: FaixaProps<Cadencia>): ReactElement {
  const tamanho = telao ? 'telao' : 'normal'
  return (
    <Faixa titulo="Cadência" pergunta="A cadência está viva?" icone={Repeat} telao={telao}>
      <div className="grid gap-3 lg:grid-cols-[minmax(0,320px)_1fr]">
        <TaxaCard
          rotulo="Relatórios Táticos no prazo"
          taxa={dados.relatoriosTaticos}
          meta={META_RT}
          cadencia="semanal"
          unidade="RTs previstos"
          explicacao={{
            fonte: 'ClickUp',
            oQueE: 'Dos Relatórios Táticos previstos na semana, quantos foram entregues no prazo.',
            formula: 'RTs entregues até o prazo ÷ RTs previstos na janela',
            origem: 'listas de cadência · tarefas F.O.R.T.E.',
            regra:
              'O RT é a tarefa “Entregar F.O.R.T.E.” que cada área abre por semana — não existe lista dedicada. A meta é 100%: cadência é binária, ou o relatório saiu ou não.',
          }}
          tamanho={tamanho}
        />

        <Card className="space-y-3 p-4 sm:p-5">
          <TituloComFonte titulo="Entrega por área" origem="clickup" campo="F.O.R.T.E." />
          {dados.detalhe.length === 0 ? (
            <EmptyState
              size="sm"
              title="Nenhum Relatório Tático previsto na janela"
              description="Sem tarefa F.O.R.T.E. com prazo no período, não há cadência para medir."
            />
          ) : (
            <ul className="grid gap-2 sm:grid-cols-2">
              {dados.detalhe.map((rt) => (
                <li
                  key={rt.id}
                  className="border-border flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-sm"
                >
                  <span className="min-w-0 truncate">{rt.setor ?? rt.nome}</span>
                  <span
                    className={
                      rt.noPrazo
                        ? 'text-success shrink-0 text-xs font-medium'
                        : rt.entregue
                          ? 'text-warning shrink-0 text-xs font-medium'
                          : 'text-destructive shrink-0 text-xs font-medium'
                    }
                  >
                    {rt.noPrazo ? 'no prazo' : rt.entregue ? 'atrasado' : 'não entregue'}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </Faixa>
  )
}
